// DC power-flow approximation: each line is a resistor (resistance
// proportional to length), each node injects (producer/discharging storage)
// or draws (consumer/charging storage) power, and per-line flow is solved
// via Kirchhoff's current law (one linear solve per connected component).
// Loops and parallel lines split load according to relative resistance —
// see doc/game_design.md's "power-grid solver" entry.
//
// Control nodes can split a junction hex into several independent graph
// nodes (see `resolveVirtualId`); every other hex — producer, consumer,
// storage, or empty — always fully joins every line touching it, exactly
// like a plain wire splice.
//
// Within a connected component, supply and demand are balanced to whichever
// is smaller before solving (a real grid can't sustain a net power
// mismatch). Any storage in the component tries to absorb a surplus
// (charging) or cover a deficit (discharging) first. Whatever's left over
// is either an imbalance (the component has a producer, so this is a
// provisioning/dial mistake) or unsatisfied demand (no producer reachable
// at all — a topology failure) — see the two-meter split in
// doc/game_design.md.

import { CONFIG, PRODUCER_TYPES, CONSUMER_TYPES } from "./config";
import type { ControlNode, GameState, GridLine, GridNode, ProducerNode, ConsumerNode, StorageNode } from "./state";
import type { WeatherDay } from "./weather";

export interface ComponentImbalance {
  mismatchMw: number;
  /** "oversupply": this island produced more than it could use/store.
   * "undersupply": this island needed more than it could produce/discharge. */
  direction: "oversupply" | "undersupply";
  /** Same producer-present-or-not distinction as the two anger meters —
   * see doc/game_design.md: true means this counts as imbalance anger,
   * false means unsatisfied anger. */
  hasProducer: boolean;
}

export interface SolveResult {
  totalSupplyPotentialMw: number; // whole-map rated potential, informational only
  /** Same rated potential as totalSupplyPotentialMw, broken down by producer
   * type id — this phase's dial and weather multiplier are already baked
   * in, so it moves with the day phase and the day's weather like the
   * total does. Keyed by every type present on the map; a type with no
   * producers currently built just doesn't appear. */
  supplyPotentialByType: Record<string, number>;
  totalDemandPotentialMw: number;
  totalServedMw: number;
  /** Supply/demand mismatch within a component that has at least one
   * producer (either wasted surplus or an unmet shortfall — direction
   * doesn't matter, it's a provisioning/dial error either way). */
  totalImbalanceMw: number;
  /** Demand from consumers with literally no producer reachable in their
   * component — a topology failure, not a quantity one. */
  totalUnsatisfiedMw: number;
  /** One entry per connected component that currently has a mismatch —
   * totalImbalanceMw/totalUnsatisfiedMw are just the sum of these, keeping
   * both a quick aggregate and the per-island detail (which island, how
   * much, which direction) available to the caller. */
  componentImbalances: ComponentImbalance[];
  lineFlowsMw: Record<string, number>; // signed flow, positive = a -> b
  saturatedLineIds: string[];
  /** Actual delivered/dispatched MW this phase, keyed by producer/consumer node id. */
  nodeServedMw: Record<string, number>;
  /** Proposed charge change this phase, keyed by storage node id. Not applied
   * by solveGrid itself — the caller decides whether to commit it. */
  storageChargeDeltaMwh: Record<string, number>;
}

export function linePairKey(lineIdA: string, lineIdB: string): string {
  return lineIdA < lineIdB ? `${lineIdA}|${lineIdB}` : `${lineIdB}|${lineIdA}`;
}

/** Active lines physically touching a hex — shared by the group-computation
 * helpers below and by the join/split editing actions in simulation.ts, so
 * "which lines can this control node even see" stays in one place. */
export function touchingActiveLineIds(state: GameState, hexId: string): string[] {
  return Object.values(state.lines)
    .filter((l) => l.status === "active" && (l.a === hexId || l.b === hexId))
    .map((l) => l.id);
}

/** UI-facing helper: for a control node, which touching active lines are
 * currently grouped (joined) together. Mirrors the grouping logic inside
 * `buildVirtualIdResolver` — kept as a small separate copy since that one
 * is a private closure scoped to a single solve pass. */
export function computeControlNodeGroups(state: GameState, hexId: string): Map<string, string> {
  const node = state.nodes[hexId];
  if (!node || node.kind !== "control") return new Map();

  const touching = touchingActiveLineIds(state, hexId).map((id) => state.lines[id]);
  const parent = new Map<string, string>();
  for (const l of touching) parent.set(l.id, l.id);

  function find(x: string): string {
    let root = x;
    while (parent.get(root) !== root) root = parent.get(root)!;
    let cur = x;
    while (parent.get(cur) !== root) {
      const next = parent.get(cur)!;
      parent.set(cur, root);
      cur = next;
    }
    return root;
  }
  function union(x: string, y: string): void {
    const rx = find(x);
    const ry = find(y);
    if (rx !== ry) parent.set(rx, ry);
  }

  const separated = new Set(node.separatedPairs);
  for (let i = 0; i < touching.length; i++) {
    for (let j = i + 1; j < touching.length; j++) {
      if (!separated.has(linePairKey(touching[i].id, touching[j].id))) {
        union(touching[i].id, touching[j].id);
      }
    }
  }

  const result = new Map<string, string>();
  for (const l of touching) result.set(l.id, find(l.id));
  return result;
}

/** A producer type not listed in the day's weather condition is unaffected
 * (defaults to 1.0) — e.g. oil never appears in outputMultiplierByType.
 * Exported so the inspector can apply the same weather math to its
 * now/projected display instead of showing raw pre-weather dial values. */
export function weatherMultiplierFor(weather: WeatherDay, typeId: string): number {
  return weather.outputMultiplierByType[typeId] ?? 1.0;
}

export function solveGrid(state: GameState, phaseIndex: number): SolveResult {
  const nodes = Object.values(state.nodes);
  const producers = nodes.filter((n): n is ProducerNode => n.kind === "producer");
  const consumers = nodes.filter((n): n is ConsumerNode => n.kind === "consumer");
  // A dismantling battery goes offline (no charge/discharge) but its hex
  // still exists for line routing, same as any other node while dismantling.
  const storages = nodes.filter((n): n is StorageNode => n.kind === "storage" && n.status === "active");

  const producerPotential = new Map<string, number>();
  const supplyPotentialByType: Record<string, number> = {};
  for (const p of producers) {
    const type = PRODUCER_TYPES[p.typeId];
    const dial = p.effectivePct / 100;
    const weatherMult = weatherMultiplierFor(state.weatherToday, type.id);
    const potential = p.capacityMw * dial * weatherMult;
    producerPotential.set(p.id, potential);
    supplyPotentialByType[type.id] = (supplyPotentialByType[type.id] ?? 0) + potential;
  }
  const consumerPotential = new Map<string, number>();
  for (const c of consumers) {
    const type = CONSUMER_TYPES[c.typeId];
    consumerPotential.set(c.id, c.capacityMw * type.demandPattern[phaseIndex]);
  }

  const totalSupplyPotentialMw = sumValues(producerPotential);
  const totalDemandPotentialMw = sumValues(consumerPotential);

  const activeLines = Object.values(state.lines).filter((l) => l.status === "active");
  const resolveVirtualId = buildVirtualIdResolver(state.nodes, activeLines);

  const graphNodeIds = new Set<string>();
  for (const p of producers) graphNodeIds.add(p.id);
  for (const c of consumers) graphNodeIds.add(c.id);
  for (const s of storages) graphNodeIds.add(s.id);
  const virtualEndpoints = new Map<string, [string, string]>(); // line.id -> [virtualA, virtualB]
  for (const line of activeLines) {
    const va = resolveVirtualId(line.a, line.id);
    const vb = resolveVirtualId(line.b, line.id);
    virtualEndpoints.set(line.id, [va, vb]);
    graphNodeIds.add(va);
    graphNodeIds.add(vb);
  }

  const components = findConnectedComponents(graphNodeIds, activeLines, virtualEndpoints);

  let totalServedMw = 0;
  let totalImbalanceMw = 0;
  let totalUnsatisfiedMw = 0;
  const componentImbalances: ComponentImbalance[] = [];
  const lineFlowsMw: Record<string, number> = {};
  const nodeServedMw: Record<string, number> = {};
  const storageChargeDeltaMwh: Record<string, number> = {};

  for (const comp of components) {
    const compSet = new Set(comp);
    const compProducers = producers.filter((p) => compSet.has(p.id));
    const compConsumers = consumers.filter((c) => compSet.has(c.id));
    const compStorages = storages.filter((s) => compSet.has(s.id));

    const supply = sumIds(compProducers.map((p) => p.id), producerPotential);
    const demand = sumIds(compConsumers.map((c) => c.id), consumerPotential);

    const injections: Record<string, number> = {};
    for (const id of comp) injections[id] = 0;

    // Whether this component has any producer at all decides which meter a
    // mismatch counts against: a producer present but mis-provisioned (too
    // much or too little for its demand) is an *imbalance* — you built or
    // dialed the wrong amount. A consumer with no producer anywhere in its
    // component has no source to be mismatched with at all — it's simply
    // *unsatisfied*, a topology failure, not a quantity one.
    const hasProducer = compProducers.length > 0;

    // "charge"/"discharge" storages are a hard override: they always do
    // that, at up to their rate/headroom-or-availability limit, regardless
    // of whether their island even wants it — so their flow is computed
    // unconditionally, up front, unlike "auto" storages (below) which only
    // act on whatever's left after these forced flows are accounted for.
    const forcedChargeStorages = compStorages.filter((s) => s.mode === "charge");
    const forcedDischargeStorages = compStorages.filter((s) => s.mode === "discharge");
    const autoStorages = compStorages.filter((s) => s.mode === "auto");

    const forcedChargeAmounts = forcedChargeStorages.map((s) => Math.max(0, Math.min(s.rateMw, s.capacityMwh - s.chargeMwh)));
    const forcedChargeTotal = forcedChargeAmounts.reduce((a, b) => a + b, 0);
    const forcedDischargeAmounts = forcedDischargeStorages.map((s) => Math.max(0, Math.min(s.rateMw, s.chargeMwh)));
    const forcedDischargeTotal = forcedDischargeAmounts.reduce((a, b) => a + b, 0);

    forcedChargeStorages.forEach((s, i) => {
      storageChargeDeltaMwh[s.id] = forcedChargeAmounts[i];
      injections[s.id] = -forcedChargeAmounts[i]; // draws power, like a consumer
    });
    forcedDischargeStorages.forEach((s, i) => {
      storageChargeDeltaMwh[s.id] = -forcedDischargeAmounts[i];
      injections[s.id] = forcedDischargeAmounts[i]; // supplies power, like a producer
    });

    // Forced flows fold into supply/demand exactly like an extra
    // producer/consumer would — everything below is the same
    // curtail-the-flexible-side logic as before, just against these
    // "effective" totals instead of the raw producer/consumer ones.
    const effectiveSupply = supply + forcedDischargeTotal;
    const effectiveDemand = demand + forcedChargeTotal;

    if (effectiveSupply >= effectiveDemand) {
      const surplus = effectiveSupply - effectiveDemand;
      const headrooms = autoStorages.map((s) => Math.max(0, Math.min(s.rateMw, s.capacityMwh - s.chargeMwh)));
      const totalHeadroom = headrooms.reduce((a, b) => a + b, 0);
      const autoCharged = Math.min(surplus, totalHeadroom);
      const mismatch = surplus - autoCharged;

      totalServedMw += effectiveDemand + autoCharged;
      if (hasProducer) totalImbalanceMw += mismatch;
      else totalUnsatisfiedMw += mismatch; // unreachable in practice (surplus needs a producer) but kept for symmetry
      if (mismatch > 0.01) componentImbalances.push({ mismatchMw: mismatch, direction: "oversupply", hasProducer });

      // Only producers get curtailed here — forced-discharge already got
      // its full requested amount above, and demand (consumers + forced
      // charge) is fully served in a surplus, so producers are the only
      // flexible side left to absorb the difference.
      const producersNeeded = Math.max(0, effectiveDemand + autoCharged - forcedDischargeTotal);
      const supplyScale = supply > 0 ? producersNeeded / supply : 0;
      for (const p of compProducers) {
        const served = (producerPotential.get(p.id) ?? 0) * supplyScale;
        nodeServedMw[p.id] = served;
        injections[p.id] = served;
      }
      for (const c of compConsumers) {
        const served = consumerPotential.get(c.id) ?? 0;
        nodeServedMw[c.id] = served;
        injections[c.id] = -served;
      }
      autoStorages.forEach((s, i) => {
        const share = totalHeadroom > 0 ? (headrooms[i] / totalHeadroom) * autoCharged : 0;
        storageChargeDeltaMwh[s.id] = share;
        injections[s.id] = -share; // charging draws power, like a consumer
      });
    } else {
      const deficit = effectiveDemand - effectiveSupply;
      const availabilities = autoStorages.map((s) => Math.max(0, Math.min(s.rateMw, s.chargeMwh)));
      const totalAvailable = availabilities.reduce((a, b) => a + b, 0);
      const autoDischarged = Math.min(deficit, totalAvailable);
      const mismatch = deficit - autoDischarged;

      totalServedMw += effectiveSupply + autoDischarged;
      if (hasProducer) totalImbalanceMw += mismatch;
      else totalUnsatisfiedMw += mismatch;
      if (mismatch > 0.01) componentImbalances.push({ mismatchMw: mismatch, direction: "undersupply", hasProducer });

      // Only consumers get curtailed here — forced-charge already drew its
      // full requested amount above regardless of this island's deficit,
      // so consumers are the ones who lose out first.
      const consumersNeeded = Math.max(0, effectiveSupply + autoDischarged - forcedChargeTotal);
      const demandScale = demand > 0 ? consumersNeeded / demand : 0;
      for (const p of compProducers) {
        const served = producerPotential.get(p.id) ?? 0;
        nodeServedMw[p.id] = served;
        injections[p.id] = served;
      }
      for (const c of compConsumers) {
        const served = (consumerPotential.get(c.id) ?? 0) * demandScale;
        nodeServedMw[c.id] = served;
        injections[c.id] = -served;
      }
      autoStorages.forEach((s, i) => {
        const share = totalAvailable > 0 ? (availabilities[i] / totalAvailable) * autoDischarged : 0;
        storageChargeDeltaMwh[s.id] = -share;
        injections[s.id] = share; // discharging supplies power, like a producer
      });
    }

    const compLines = activeLines.filter((l) => {
      const [va, vb] = virtualEndpoints.get(l.id)!;
      return compSet.has(va) && compSet.has(vb);
    });
    const flows = solveComponentFlows(comp, compLines, virtualEndpoints, injections);
    Object.assign(lineFlowsMw, flows);
  }

  const saturatedLineIds = activeLines
    .filter((l) => Math.abs(lineFlowsMw[l.id] ?? 0) > CONFIG.lineCapacityMw)
    .map((l) => l.id);

  return {
    totalSupplyPotentialMw,
    supplyPotentialByType,
    totalDemandPotentialMw,
    totalServedMw,
    totalImbalanceMw,
    totalUnsatisfiedMw,
    componentImbalances,
    lineFlowsMw,
    saturatedLineIds,
    nodeServedMw,
    storageChargeDeltaMwh,
  };
}

function sumValues(m: Map<string, number>): number {
  let total = 0;
  for (const v of m.values()) total += v;
  return total;
}

function sumIds(ids: string[], m: Map<string, number>): number {
  let total = 0;
  for (const id of ids) total += m.get(id) ?? 0;
  return total;
}

/** A plain hex (producer/consumer/storage/empty) always fully joins every
 * line touching it. A control-node hex instead partitions its touching
 * lines into groups (see doc/game_design.md); lines in different groups are
 * electrically separate at that hex even though they share the same tile. */
function buildVirtualIdResolver(
  allNodes: Record<string, GridNode>,
  activeLines: GridLine[],
): (hexId: string, lineId: string) => string {
  const groupCache = new Map<string, Map<string, string>>();

  function computeGroups(hexId: string, controlNode: ControlNode): Map<string, string> {
    const touching = activeLines.filter((l) => l.a === hexId || l.b === hexId);
    const parent = new Map<string, string>();
    for (const l of touching) parent.set(l.id, l.id);

    function find(x: string): string {
      let root = x;
      while (parent.get(root) !== root) root = parent.get(root)!;
      let cur = x;
      while (parent.get(cur) !== root) {
        const next = parent.get(cur)!;
        parent.set(cur, root);
        cur = next;
      }
      return root;
    }
    function union(x: string, y: string): void {
      const rx = find(x);
      const ry = find(y);
      if (rx !== ry) parent.set(rx, ry);
    }

    const separated = new Set(controlNode.separatedPairs);
    for (let i = 0; i < touching.length; i++) {
      for (let j = i + 1; j < touching.length; j++) {
        if (!separated.has(linePairKey(touching[i].id, touching[j].id))) {
          union(touching[i].id, touching[j].id);
        }
      }
    }

    const result = new Map<string, string>();
    for (const l of touching) result.set(l.id, `${hexId}#${find(l.id)}`);
    return result;
  }

  return (hexId: string, lineId: string): string => {
    const node = allNodes[hexId];
    // A dismantling control node goes offline: it reverts to plain
    // full-join behavior until it either comes back online or is removed.
    if (!node || node.kind !== "control" || node.status !== "active") return hexId;
    let groups = groupCache.get(hexId);
    if (!groups) {
      groups = computeGroups(hexId, node);
      groupCache.set(hexId, groups);
    }
    return groups.get(lineId) ?? hexId;
  };
}

function findConnectedComponents(
  nodeIds: Set<string>,
  lines: GridLine[],
  virtualEndpoints: Map<string, [string, string]>,
): string[][] {
  const adjacency = new Map<string, string[]>();
  for (const id of nodeIds) adjacency.set(id, []);
  for (const line of lines) {
    const [va, vb] = virtualEndpoints.get(line.id)!;
    adjacency.get(va)?.push(vb);
    adjacency.get(vb)?.push(va);
  }

  const visited = new Set<string>();
  const components: string[][] = [];
  for (const start of nodeIds) {
    if (visited.has(start)) continue;
    const queue = [start];
    visited.add(start);
    const comp: string[] = [];
    while (queue.length > 0) {
      const current = queue.shift()!;
      comp.push(current);
      for (const next of adjacency.get(current) ?? []) {
        if (!visited.has(next)) {
          visited.add(next);
          queue.push(next);
        }
      }
    }
    components.push(comp);
  }
  return components;
}

/** Nodal analysis on a resistor network: solve for potentials (one node
 * grounded as reference), then derive per-line flow from the potential
 * difference across each line's resistance. */
function solveComponentFlows(
  nodeIds: string[],
  lines: GridLine[],
  virtualEndpoints: Map<string, [string, string]>,
  injections: Record<string, number>,
): Record<string, number> {
  const flows: Record<string, number> = {};
  if (nodeIds.length <= 1 || lines.length === 0) {
    for (const line of lines) flows[line.id] = 0;
    return flows;
  }

  const conductanceOf = (line: GridLine) => 1 / Math.max(line.lengthKm * CONFIG.lineResistancePerKm, 0.01);

  const referenceId = nodeIds[0];
  const others = nodeIds.slice(1);
  const index = new Map(others.map((id, i) => [id, i]));
  const n = others.length;
  const A: number[][] = Array.from({ length: n }, () => new Array(n).fill(0));
  const b = others.map((id) => injections[id] ?? 0);

  for (const line of lines) {
    const [va, vb] = virtualEndpoints.get(line.id)!;
    const g = conductanceOf(line);
    const ia = index.get(va);
    const ib = index.get(vb);
    if (ia !== undefined) A[ia][ia] += g;
    if (ib !== undefined) A[ib][ib] += g;
    if (ia !== undefined && ib !== undefined) {
      A[ia][ib] -= g;
      A[ib][ia] -= g;
    }
  }

  const x = solveLinearSystem(A, b);
  const potentials: Record<string, number> = { [referenceId]: 0 };
  others.forEach((id, i) => {
    potentials[id] = x[i];
  });

  for (const line of lines) {
    const [va, vb] = virtualEndpoints.get(line.id)!;
    const g = conductanceOf(line);
    flows[line.id] = (potentials[va] - potentials[vb]) * g;
  }
  return flows;
}

/** Dense Gaussian elimination with partial pivoting. Components are small
 * (well under 100 nodes for this prototype) so a direct solve is plenty
 * fast; no need for iterative/warm-started solving yet. */
function solveLinearSystem(matrix: number[][], rhs: number[]): number[] {
  const n = rhs.length;
  const A = matrix.map((row) => row.slice());
  const b = rhs.slice();

  for (let col = 0; col < n; col++) {
    let pivotRow = col;
    let maxAbs = Math.abs(A[col][col]);
    for (let row = col + 1; row < n; row++) {
      const v = Math.abs(A[row][col]);
      if (v > maxAbs) {
        maxAbs = v;
        pivotRow = row;
      }
    }
    if (pivotRow !== col) {
      [A[col], A[pivotRow]] = [A[pivotRow], A[col]];
      [b[col], b[pivotRow]] = [b[pivotRow], b[col]];
    }
    const pivot = A[col][col];
    if (Math.abs(pivot) < 1e-9) continue; // singular (shouldn't happen for a connected graph)
    for (let row = col + 1; row < n; row++) {
      const factor = A[row][col] / pivot;
      if (factor === 0) continue;
      for (let c = col; c < n; c++) A[row][c] -= factor * A[col][c];
      b[row] -= factor * b[col];
    }
  }

  const x = new Array(n).fill(0);
  for (let row = n - 1; row >= 0; row--) {
    let sum = b[row];
    for (let c = row + 1; c < n; c++) sum -= A[row][c] * x[c];
    x[row] = Math.abs(A[row][row]) < 1e-9 ? 0 : sum / A[row][row];
  }
  return x;
}
